import { useState, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Alert,
  Skeleton,
  Snackbar,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SendToMobileRoundedIcon from "@mui/icons-material/SendToMobileRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  listFinkedaTransactions,
  listFinkedaAdminTransactions,
  finkedaAdminEarnings,
  refreshFinkedaTransaction,
  getBalance,
} from "../api";

const isAdmin = () => localStorage.getItem("roleName") === "ADMIN";

/* Label a commission entry by its role/kind for the breakdown dialog. */
const entryLabel = (e) => {
  if (e.kind === "self") return `${e.name || "Payer"} (self)`;
  if (e.toRoleLevel === 1) return `${e.name || "Admin"} (admin margin)`;
  return e.name || e.roleName || "Upline";
};

/* ---------------------------------- helpers ---------------------------------- */

const LIMIT = 20;

const formatINR = (v) =>
  v == null || v === "" || isNaN(Number(v))
    ? "—"
    : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const maskAccount = (acc = "") => {
  const s = String(acc);
  return s.length > 4 ? `•••• ${s.slice(-4)}` : s || "—";
};

/* Drop internal branding from any gateway label shown to the customer */
const cleanLabel = (s) =>
  String(s || "")
    .replace(/finkeda/gi, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const STATUS_META = {
  success: { label: "Success", color: "success" },
  failed: { label: "Failed", color: "error" },
  pending: { label: "Pending", color: "warning" },
};

const statusMeta = (status) =>
  STATUS_META[String(status || "").toLowerCase()] || {
    label: status || "—",
    color: "default",
  };

function StatusChip({ status }) {
  const meta = statusMeta(status);
  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.color}
      variant={meta.color === "default" ? "outlined" : "filled"}
      sx={{ fontWeight: 600, fontSize: 11 }}
    />
  );
}

const isPending = (status) => String(status || "").toLowerCase() === "pending";

/* ================================== page ================================== */

export default function FinkedaTransactions() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [snack, setSnack] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [view, setView] = useState("mine"); // mine | all | earnings (admin)
  const [earnings, setEarnings] = useState(null);
  const [detail, setDetail] = useState(null);
  const admin = isAdmin();

  useEffect(() => {
    getBalance()
      .then(({ data }) => {
        const wallet = (data?.commissionWallets || []).find(
          (w) => w.gateway === "finkeda",
        );
        setWalletBalance(Number(wallet?.balance) || 0);
      })
      .catch(() => setWalletBalance(null));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    if (admin && view === "earnings") {
      finkedaAdminEarnings()
        .then(({ data }) => setEarnings(data))
        .catch((e) =>
          setError(e.response?.data?.error || "Failed to load earnings"),
        )
        .finally(() => setLoading(false));
      return;
    }
    const fetcher =
      admin && view === "all"
        ? listFinkedaAdminTransactions
        : listFinkedaTransactions;
    fetcher({ page, limit: LIMIT })
      .then(({ data }) => {
        setRows(data.transactions || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch((e) =>
        setError(e.response?.data?.error || "Failed to load transactions"),
      )
      .finally(() => setLoading(false));
  }, [page, admin, view]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefreshRow = async (id) => {
    setRefreshingId(id);
    try {
      const { data } = await refreshFinkedaTransaction(id);
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: data.status } : r)),
      );
      setSnack({
        severity: "success",
        msg: `Status updated: ${statusMeta(data.status).label}`,
      });
    } catch (e) {
      setSnack({
        severity: "error",
        msg: e.response?.data?.error || "Failed to refresh status",
      });
    }
    setRefreshingId(null);
  };

  const pageCount = Math.ceil(total / LIMIT);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {admin && view === "earnings"
              ? "Card→Bank Earnings"
              : admin && view === "all"
                ? "All Card→Bank Payments"
                : "My Card→Bank Payments"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {admin && view === "earnings"
              ? "Per user: transactions, volume and what each side earned"
              : admin && view === "all"
                ? "Every Card→Bank payout — open a row to see who earned what"
                : "Card-funded payouts to beneficiary bank accounts"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {admin && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, v) => {
                if (v) {
                  setPage(1);
                  setView(v);
                }
              }}
              sx={{ mr: 0.5 }}
            >
              <ToggleButton value="mine" sx={{ textTransform: "none" }}>
                Mine
              </ToggleButton>
              <ToggleButton value="all" sx={{ textTransform: "none" }}>
                All
              </ToggleButton>
              <ToggleButton value="earnings" sx={{ textTransform: "none" }}>
                Earnings
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button
            component={RouterLink}
            to="/finkeda"
            variant="contained"
            size="small"
            startIcon={<SendToMobileRoundedIcon />}
            sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 700 }}
          >
            New Payment
          </Button>
          <Tooltip title="Refresh" arrow>
            <IconButton size="small" onClick={load}>
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {walletBalance != null && (
        <Card sx={{ p: 2, mb: 3, borderRadius: 3, maxWidth: 320 }}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Avatar
              sx={{
                bgcolor: (t) => alpha(t.palette.success.main, 0.14),
                color: "success.main",
                width: 40,
                height: 40,
              }}
            >
              <AccountBalanceWalletRoundedIcon />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" noWrap>
                Card→Bank Earnings
              </Typography>
              <Typography variant="h6" fontWeight={700} noWrap>
                {formatINR(walletBalance)}
              </Typography>
            </Box>
          </Stack>
        </Card>
      )}

      {admin && view === "earnings" ? (
        <EarningsTable earnings={earnings} loading={loading} />
      ) : (
      <Card sx={{ overflow: "hidden", borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} height={44} sx={{ mb: 1 }} />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <ReceiptLongRoundedIcon
              sx={{ fontSize: 52, color: "text.disabled", mb: 1 }}
            />
            <Typography fontWeight={600} mb={0.5}>
              No payments yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Card-funded payouts you make will appear here
            </Typography>
            <Button
              component={RouterLink}
              to="/finkeda"
              variant="outlined"
              startIcon={<SendToMobileRoundedIcon />}
              sx={{ borderRadius: 2.5, textTransform: "none" }}
            >
              Make your first payment
            </Button>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      color: "text.secondary",
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>Date</TableCell>
                  {admin && view === "all" && <TableCell>User</TableCell>}
                  <TableCell>Gateway</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Net to bank</TableCell>
                  <TableCell>Beneficiary</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell
                      sx={{ color: "text.secondary", fontSize: 13, whiteSpace: "nowrap" }}
                    >
                      {formatDate(r.created_at)}
                    </TableCell>
                    {admin && view === "all" && (
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {r.initiatorName || "—"}
                          </Typography>
                          {r.initiatorRole && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {String(r.initiatorRole).replace(/_/g, " ")}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {r.purpose ||
                            cleanLabel(r.pg_type) ||
                            cleanLabel(r.gateway_key) ||
                            "—"}
                        </Typography>
                        {r.network && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {r.network}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {formatINR(r.amount)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        {formatINR(r.net_to_bank)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {r.sender_name || "—"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {maskAccount(r.sender_account)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={r.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Commission breakdown" arrow>
                        <IconButton size="small" onClick={() => setDetail(r)}>
                          <VisibilityRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isPending(r.status) && (
                        <Tooltip title="Check status" arrow>
                          <span>
                            <IconButton
                              size="small"
                              disabled={refreshingId === r.id}
                              onClick={() => handleRefreshRow(r.id)}
                            >
                              <RefreshRoundedIcon
                                fontSize="small"
                                sx={
                                  refreshingId === r.id
                                    ? {
                                        animation: "finkeda-spin 0.9s linear infinite",
                                        "@keyframes finkeda-spin": {
                                          from: { transform: "rotate(0deg)" },
                                          to: { transform: "rotate(360deg)" },
                                        },
                                      }
                                    : undefined
                                }
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {pageCount > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>
      )}

      <BreakdownDialog
        txn={detail}
        showUser={admin && view === "all"}
        onClose={() => setDetail(null)}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            sx={{ borderRadius: 2 }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

/* ---------------------------- earnings by user ---------------------------- */

function EarningsTable({ earnings, loading }) {
  if (loading) {
    return (
      <Card sx={{ p: 3, borderRadius: 3 }}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={44} sx={{ mb: 1 }} />
        ))}
      </Card>
    );
  }
  const users = earnings?.users || [];
  const totals = earnings?.totals || {};
  if (users.length === 0) {
    return (
      <Card sx={{ p: 6, borderRadius: 3, textAlign: "center" }}>
        <ReceiptLongRoundedIcon
          sx={{ fontSize: 52, color: "text.disabled", mb: 1 }}
        />
        <Typography fontWeight={600}>No earnings yet</Typography>
        <Typography variant="body2" color="text.secondary">
          Once users make Card→Bank payments, their earnings appear here.
        </Typography>
      </Card>
    );
  }
  return (
    <Card sx={{ overflow: "hidden", borderRadius: 3 }}>
      <Box sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontWeight: 700,
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  color: "text.secondary",
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell>User</TableCell>
              <TableCell align="right">Transactions</TableCell>
              <TableCell align="right">Volume</TableCell>
              <TableCell align="right">They earned</TableCell>
              <TableCell align="right">Admin earned</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.userId} hover>
                <TableCell>
                  <Stack spacing={0.25}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {u.name}
                    </Typography>
                    {u.roleName && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {String(u.roleName).replace(/_/g, " ")}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{u.txnCount}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                  {formatINR(u.volume)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {formatINR(u.selfEarned)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 700, color: "success.main", whiteSpace: "nowrap" }}
                >
                  {formatINR(u.adminEarned)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ "& td": { fontWeight: 800, borderTop: "2px solid", borderColor: "divider" } }}>
              <TableCell>Total</TableCell>
              <TableCell align="right">{totals.txnCount || 0}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {formatINR(totals.volume)}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {formatINR(totals.selfEarned)}
              </TableCell>
              <TableCell align="right" sx={{ color: "success.main", whiteSpace: "nowrap" }}>
                {formatINR(totals.adminEarned)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
}

/* ---------------------------- breakdown dialog ---------------------------- */

function Line({ label, value, strong, color }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 0.5 }}
    >
      <Typography variant="body2" color={strong ? "text.primary" : "text.secondary"}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={strong ? 700 : 600}
        color={color || "text.primary"}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function BreakdownDialog({ txn, showUser, onClose }) {
  const admin = isAdmin();
  if (!txn) return null;

  const entries = txn.breakdownEntries || [];
  const self = Number(entries.find((e) => e.kind === "self")?.amount || 0);
  const flatFee = Number(txn.flat_fee || 0);
  const payout = Number(txn.payout_fee || 0);
  const cf = Number(txn.finkeda_charge_amt || 0);
  const markup = Number(txn.markup_amount || 0);
  /* Consumer view: everything that isn't the payer's own commission and isn't
     the net, split into the gateway+chain charge, payout and flat fee. */
  const charges =
    cf +
    entries
      .filter((e) => e.kind !== "self")
      .reduce((s, e) => s + Number(e.amount || 0), 0);
  const pct = (v) =>
    v == null || v === "" || isNaN(Number(v)) ? "" : ` (${Number(v)}%)`;

  return (
    <Dialog open={!!txn} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>
          Card→Bank breakdown
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {cleanLabel(txn.pg_type) || cleanLabel(txn.gateway_key)} · {txn.network} ·{" "}
          {formatDate(txn.created_at)}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {showUser && (
          <Alert severity="info" icon={false} sx={{ mb: 1.5, borderRadius: 2 }}>
            Paid by <b>{txn.initiatorName || "—"}</b>
            {txn.initiatorRole
              ? ` (${String(txn.initiatorRole).replace(/_/g, " ")})`
              : ""}
          </Alert>
        )}

        <Box sx={{ mb: 1 }}>
          <StatusChip status={txn.status} />
        </Box>

        {admin ? (
          <>
            <Typography variant="overline" color="text.secondary">
              Money flow
            </Typography>
            <Line label="Amount charged on card" value={formatINR(txn.amount)} strong />
            <Line
              label={`Provider gateway charge${pct(txn.finkeda_charge_pct)}`}
              value={`− ${formatINR(cf)}`}
            />
            <Line
              label={`Markup returned to admin${pct(txn.markup_pct)}`}
              value={`− ${formatINR(markup)}`}
            />
            <Divider sx={{ my: 1 }} />
            <Line
              label="Net to beneficiary bank"
              value={formatINR(txn.net_to_bank)}
              strong
              color="success.main"
            />

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="overline" color="text.secondary">
              Markup credited ({formatINR(markup)})
            </Typography>
            {entries.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                No commission entries recorded.
              </Typography>
            )}
            {entries.map((e, i) => (
              <Line
                key={i}
                label={`${entryLabel(e)}${pct(e.percentage)}`}
                value={formatINR(e.amount)}
                color="primary.main"
              />
            ))}
            {flatFee > 0 && (
              <Line
                label="Flat platform fee → admin"
                value={formatINR(flatFee)}
                color="primary.main"
              />
            )}
            {payout > 0 && (
              <Line
                label="Payout fee → admin (payable to provider)"
                value={formatINR(payout)}
                color="primary.main"
              />
            )}

            {txn.provider_request && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="overline" color="text.secondary">
                  Sent to provider (markup {txn.markup_pct}%)
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
                    fontSize: 11,
                    fontFamily: "monospace",
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {JSON.stringify(txn.provider_request, null, 2)}
                </Box>
              </>
            )}
          </>
        ) : (
          <>
            <Typography variant="overline" color="text.secondary">
              Money flow
            </Typography>
            <Line label="Amount charged on card" value={formatINR(txn.amount)} strong />
            <Line label="Charges" value={`− ${formatINR(charges)}`} />
            <Line label="Payout (incl. GST)" value={`− ${formatINR(payout)}`} />
            <Line label="Platform charges" value={`− ${formatINR(flatFee)}`} />
            {self > 0 && (
              <Line
                label="Your commission"
                value={formatINR(self)}
                color="primary.main"
                strong
              />
            )}
            <Divider sx={{ my: 1 }} />
            <Line
              label="Amount to be paid"
              value={formatINR(txn.net_to_bank)}
              strong
              color="success.main"
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
